<script setup lang="ts">
	import {
		type DateValue,
		getLocalTimeZone,
		parseDate,
		today,
	} from '@internationalized/date'
	import { CalendarRange, X } from 'lucide-vue-next'
	import type { DateRange } from 'reka-ui'
	import { computed, ref } from 'vue'

	import {
		Popover,
		PopoverContent,
		PopoverTrigger,
	} from '@/components/ui/popover'
	import { RangeCalendar } from '@/components/ui/range-calendar'

	/**
	 * Rango de fechas en un solo campo: botón con el periodo elegido y calendario de
	 * rango en un popover, con atajos para los rangos comunes. Trabaja con fechas
	 * `yyyy-MM-dd` (sin hora), que es como las guarda el backend.
	 */
	withDefaults(
		defineProps<{
			id?: string
			label?: string
			placeholder?: string
			error?: string
			/** No deja elegir días anteriores a hoy. */
			futureOnly?: boolean
		}>(),
		{
			id: 'date-range',
			label: '',
			placeholder: 'Elige el periodo',
			error: '',
			futureOnly: false,
		}
	)

	const from = defineModel<string>('from', { default: '' })
	const until = defineModel<string>('until', { default: '' })

	const open = ref(false)
	const now = today(getLocalTimeZone())

	const range = computed<DateRange>({
		get: () => ({
			start: from.value ? parseDate(from.value) : undefined,
			end: until.value ? parseDate(until.value) : undefined,
		}),
		set: (value) => {
			from.value = value.start?.toString() ?? ''
			until.value = value.end?.toString() ?? ''
			if (value.start && value.end) open.value = false
		},
	})

	const format = (value: string) =>
		new Date(`${value}T00:00:00`).toLocaleDateString('es-MX', {
			day: 'numeric',
			month: 'short',
			year: 'numeric',
		})

	const display = computed(() => {
		if (from.value && until.value)
			return `${format(from.value)} – ${format(until.value)}`
		if (from.value) return `Desde ${format(from.value)}`
		if (until.value) return `Hasta ${format(until.value)}`
		return ''
	})

	const days = computed(() => {
		if (!from.value || !until.value) return null
		return parseDate(until.value).compare(parseDate(from.value)) + 1
	})

	const presets: { label: string; build: () => [DateValue, DateValue] }[] = [
		{ label: 'Próximos 7 días', build: () => [now, now.add({ days: 6 })] },
		{
			label: 'Este mes',
			build: () => [
				now.set({ day: 1 }),
				now.set({ day: now.calendar.getDaysInMonth(now) }),
			],
		},
		{ label: 'Próximos 30 días', build: () => [now, now.add({ days: 29 })] },
		{
			label: 'Siguiente mes',
			build: () => {
				const start = now.add({ months: 1 }).set({ day: 1 })
				return [start, start.set({ day: start.calendar.getDaysInMonth(start) })]
			},
		},
	]

	const applyPreset = (build: () => [DateValue, DateValue]) => {
		const [start, end] = build()
		range.value = { start, end }
	}

	const clear = () => {
		from.value = ''
		until.value = ''
	}
</script>

<template>
	<div class="space-y-1.5">
		<label
			v-if="label"
			:for="id"
			class="block text-sm font-medium text-gray-700"
		>
			{{ label }}
		</label>

		<Popover v-model:open="open">
			<PopoverTrigger as-child>
				<button
					:id="id"
					type="button"
					:aria-invalid="Boolean(error)"
					:class="[
						'flex h-11 w-full items-center gap-2 rounded-xl border bg-white px-3 text-left text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-[#021F58]/20',
						error ? 'border-red-400' : (
							'border-gray-300 hover:border-gray-400 focus:border-[#021F58]'
						),
					]"
				>
					<CalendarRange class="h-4 w-4 shrink-0 text-gray-500" />
					<span
						:class="[
							'flex-1 truncate text-sm',
							display ? 'text-gray-900' : 'text-gray-400',
						]"
					>
						{{ display || placeholder }}
					</span>
					<span
						v-if="days"
						class="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600"
					>
						{{ days }} {{ days === 1 ? 'día' : 'días' }}
					</span>
				</button>
			</PopoverTrigger>
			<PopoverContent
				class="w-auto p-0"
				align="start"
			>
				<div class="flex flex-col sm:flex-row">
					<div
						class="flex flex-wrap gap-1 border-b p-2 sm:w-40 sm:flex-col sm:border-b-0 sm:border-r"
					>
						<button
							v-for="preset in presets"
							:key="preset.label"
							type="button"
							class="rounded-lg px-2.5 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-100"
							@click="applyPreset(preset.build)"
						>
							{{ preset.label }}
						</button>
						<button
							v-if="from || until"
							type="button"
							class="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-left text-sm text-red-600 hover:bg-red-50"
							@click="clear"
						>
							<X class="h-3.5 w-3.5" /> Quitar fechas
						</button>
					</div>
					<RangeCalendar
						v-model="range"
						locale="es-MX"
						:number-of-months="1"
						:min-value="futureOnly ? now : undefined"
						initial-focus
					/>
				</div>
			</PopoverContent>
		</Popover>

		<p
			v-if="error"
			class="text-xs text-red-500"
		>
			{{ error }}
		</p>
	</div>
</template>
